// src/components/pro/ProLocked.tsx — 2-oct-2026
// ============================================================================
// El bloqueo ÚNICO de las funciones PRO para un negocio gratis: qué resuelve, 3
// beneficios, un ejemplo de «así se ve» y un solo botón. Los textos viven en
// proFeatures.ts. La página conserva SU encabezado; esto va debajo.
//
//   <ProGate feature="tags">…contenido real…</ProGate>
//   <ProLocked feature="team-invite" compact />   ← recuadro dentro de una página abierta
//
// Decide con isPro(subscription), el mismo criterio que el servidor (lib/proGate.js):
// la prueba gratis cuenta como PRO (api/billingApi.ts mapStatus).
// OJO: en pantallas de WhatsApp esto cubre SOLO al gratis; un PRO sin WhatsApp
// activado sigue viendo WhatsAppRequestPanel (solicitar / en proceso).
// ============================================================================
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Crown, Check, ArrowRight, MessageCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { isPro } from '../../utils/planPermissions';
import WhatsAppPreviewBubble from '../../features/whatsapp/components/WhatsAppPreviewBubble';
import { proFeature, type ProFeatureKey, type ProExample } from './proFeatures';
import { analyticsApi } from '../../api/analyticsApi';

const TONO: Record<string, string> = {
  ok: 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
  warn: 'bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300',
  bad: 'bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-300',
  none: 'bg-surface-2 text-content-2',
};

const Ejemplo: React.FC<{ ex: ProExample }> = ({ ex }) => {
  if (ex.kind === 'bubble') {
    return (
      <div>
        <p className="text-xs text-content-3 text-center mb-2">{ex.to}</p>
        <WhatsAppPreviewBubble message={ex.message} />
      </div>
    );
  }
  if (ex.kind === 'rows') {
    return (
      <div className="rounded-lg border border-line bg-surface divide-y divide-line overflow-hidden" aria-label="Ejemplo">
        {ex.rows.map((r) => (
          <div key={r.primary} className="px-3 py-2.5 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-content truncate">{r.primary}</p>
              <p className="text-xs text-content-3">{r.secondary}</p>
            </div>
            {r.badge && <span className={`shrink-0 text-xs font-medium px-2 py-0.5 rounded-full ${TONO[r.tone || 'none']}`}>{r.badge}</span>}
          </div>
        ))}
        <p className="px-3 py-1.5 text-[11px] text-content-3 text-center bg-surface-2">Ejemplo con datos ficticios</p>
      </div>
    );
  }
  if (ex.kind === 'chips') {
    return (
      <div className="rounded-lg border border-line bg-surface p-4" aria-label="Ejemplo">
        <div className="flex flex-wrap gap-2">
          {ex.chips.map((c) => (
            <span key={c} className="text-sm px-3 py-1 rounded-full bg-accent/10 text-accent font-medium">{c}</span>
          ))}
        </div>
        <p className="mt-3 text-[11px] text-content-3 text-center">{ex.caption} · ejemplo</p>
      </div>
    );
  }
  // giftcard
  return (
    <div aria-label="Ejemplo">
      <div className="mx-auto max-w-xs aspect-[1.6] rounded-2xl p-5 text-white shadow-lg bg-gradient-to-br from-rose-500 via-fuchsia-500 to-indigo-500 flex flex-col justify-between">
        <span className="text-xs font-semibold uppercase tracking-widest opacity-90">Gift Card</span>
        <div>
          <p className="text-2xl font-bold">{ex.amount}</p>
          <p className="text-sm opacity-90">{ex.occasion}</p>
        </div>
        <span className="text-[11px] opacity-80">Con el logo de tu negocio</span>
      </div>
      <p className="mt-2 text-[11px] text-content-3 text-center">Ejemplo</p>
    </div>
  );
};

interface ProLockedProps {
  feature: ProFeatureKey;
  /** Recuadro chico (sin ejemplo) para bloquear UNA acción dentro de una página abierta. */
  compact?: boolean;
}

export const ProLocked: React.FC<ProLockedProps> = ({ feature, compact = false }) => {
  const navigate = useNavigate();
  const f = proFeature(feature);
  // Medición (plan F7): vista al mostrarse y clic en el botón → audit_log + GA4, por pantalla.
  useEffect(() => { analyticsApi.trackPro(feature, 'view'); }, [feature]);
  const ir = () => {
    analyticsApi.trackPro(feature, 'click');
    window.gtag?.('event', 'pro_lock_click', { feature });
    navigate(`/dashboard/subscription?desde=${encodeURIComponent(feature)}`);
  };
  const boton = (
    <button
      type="button"
      onClick={ir}
      className="inline-flex items-center justify-center px-5 py-2.5 rounded-md bg-accent-solid text-white text-sm font-semibold shadow-sm hover:opacity-90 transition-opacity"
    >
      <Crown className="h-4 w-4 mr-2" aria-hidden="true" />
      Mejorar a PRO
      <ArrowRight className="h-4 w-4 ml-2" aria-hidden="true" />
    </button>
  );

  if (compact) {
    return (
      <div data-pro-locked={feature} className="rounded-lg border border-amber-200 dark:border-amber-500/30 bg-amber-50/60 dark:bg-amber-500/5 p-4 flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold text-amber-700 dark:text-amber-300 flex items-center"><Crown className="h-3.5 w-3.5 mr-1" aria-hidden="true" />Función PRO</p>
          <p className="text-sm text-content mt-0.5">{f.pitch}</p>
        </div>
        {boton}
      </div>
    );
  }

  return (
    <div data-pro-locked={feature} className="bg-surface shadow rounded-lg overflow-hidden animate-fade-in-up">
      {/* Móvil: texto → ejemplo → botón (primero ver, luego decidir). Escritorio: texto+botón a la
          izquierda y el ejemplo ocupando toda la columna derecha. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 lg:grid-rows-[1fr_auto]">
        <div className="p-6 sm:p-8 pb-0 sm:pb-0 lg:pb-0 flex flex-col">
          <span className="self-start inline-flex items-center text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-100 dark:bg-amber-500/15 text-amber-800 dark:text-amber-300">
            <Crown className="h-3.5 w-3.5 mr-1" aria-hidden="true" />Función PRO
          </span>
          <h2 className="mt-3 text-lg sm:text-xl font-semibold text-content leading-snug">{f.pitch}</h2>
          <ul className="mt-4 space-y-2.5">
            {f.benefits.map((b) => (
              <li key={b} className="flex items-start text-sm text-content-2">
                <Check className="h-4 w-4 mr-2 mt-0.5 shrink-0 text-emerald-600 dark:text-emerald-400" aria-hidden="true" />{b}
              </li>
            ))}
          </ul>
          {f.whatsapp && (
            <p className="mt-4 flex items-center text-xs text-content-3">
              <MessageCircle className="h-3.5 w-3.5 mr-1.5 text-emerald-600 dark:text-emerald-400" aria-hidden="true" />
              Incluye conectar el WhatsApp de tu negocio
            </p>
          )}
        </div>
        <div className="mt-6 lg:mt-0 p-6 sm:p-8 bg-surface-2 border-y lg:border-y-0 lg:border-l border-line flex flex-col justify-center lg:col-start-2 lg:row-start-1 lg:row-span-2">
          <p className="text-xs font-semibold uppercase tracking-wider text-content-3 mb-3">Así se ve</p>
          <Ejemplo ex={f.example} />
        </div>
        <div className="p-6 sm:p-8 lg:pt-6 lg:col-start-1 lg:row-start-2">
          {boton}
          <p className="mt-2 text-xs text-content-3">Cancelación en cualquier momento.</p>
        </div>
      </div>
    </div>
  );
};

/** Muestra el contenido si el negocio es PRO (o está en prueba); si no, el bloqueo explicado. */
export const ProGate: React.FC<{ feature: ProFeatureKey; children: React.ReactNode }> = ({ feature, children }) => {
  const { currentUser } = useAuth();
  if (isPro(currentUser?.subscription)) return <>{children}</>;
  return <ProLocked feature={feature} />;
};

export default ProLocked;
